import React, { useEffect, useRef, useState } from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import FacilityServices from '@facility/services/FacilityServices';
import { importAssetTagCsv } from '@facility/services/AssetTagCsvImportServices';
import { displayApiError } from '@shared/api/apiClient';
import SystemActions from '@shared/actions/SystemActions';
import { IConfigurationTreeNode } from './types';

declare var InovxLoader: { t(key: string): string };

export default function AssetTagCsvImportDialog({ node, onClose }: { node: IConfigurationTreeNode; onClose: () => void }) {
    const [facilities, setFacilities] = useState<IFacility[]>([]);
    const [facilityId, setFacilityId] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const submitting = useRef(false);
    useEffect(() => {
        let active = true;
        FacilityServices.getAll().then(items => {
            if (!active) return;
            const eligible = items.filter(item => item.SchemaID === node.SchemaID || item.Schemas?.includes(node.SchemaID));
            setFacilities(eligible);
            if (eligible.length === 1) setFacilityId(eligible[0].ID);
        }).catch(() => { /* FacilityServices presents normal request diagnostics. */ })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [node.SchemaID]);

    const submit = async () => {
        if (submitting.current || !facilityId || !file || !node.AssetClassID) return;
        submitting.current = true;
        setRunning(true);
        try {
            const result = await importAssetTagCsv(facilityId, node.AssetClassID, file);
            // Standard message controls render HTML: uploaded filenames must remain plain text.
            const message = result.Message.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]!));
            const show = result.Success ? SystemActions.PublicActions.displaySuccessMessage : SystemActions.PublicActions.displayErrorMessage;
            show({ message, errorId: result.DiagnosticId });
            onClose();
        } catch (error) {
            displayApiError(error);
        } finally {
            submitting.current = false;
            setRunning(false);
        }
    };

    return <DialogComponent target="#app" width="440px" visible isModal showCloseIcon={!running} zIndex={110000}
        header={InovxLoader.t('facility:csvImport.title')} beforeClose={args => { args.cancel = submitting.current; }} close={onClose}
        footerTemplate={() => <div>
            <button type="button" className="btn btn-primary" disabled={loading || running || !facilityId || !file} onClick={() => void submit()}>
                {InovxLoader.t(running ? 'facility:csvImport.importing' : 'facility:csvImport.import')}
            </button>{' '}
            <button type="button" className="btn btn-default" disabled={running} onClick={onClose}>{InovxLoader.t('facility:preferences.cancelButton')}</button>
        </div>}>
        <div className="dialogContent">
            <label htmlFor="csv-import-class">{InovxLoader.t('facility:csvImport.assetClass')}</label>
            <input id="csv-import-class" className="form-control" value={node.Name} readOnly />
            <label htmlFor="csv-import-facility">{InovxLoader.t('facility:csvImport.facility')}</label>
            <select id="csv-import-facility" className="form-control" value={facilityId} disabled={loading || running} onChange={e => setFacilityId(e.target.value)}>
                <option value="">{InovxLoader.t('facility:csvImport.selectFacility')}</option>
                {facilities.map(item => <option key={item.ID} value={item.ID}>{item.Name}</option>)}
            </select>
            {!loading && facilities.length === 0 && <p>{InovxLoader.t('facility:csvImport.noFacilities')}</p>}
            <label htmlFor="csv-import-file">{InovxLoader.t('facility:csvImport.file')}</label>
            <input id="csv-import-file" type="file" accept=".csv,text/csv" disabled={running} onChange={e => setFile(e.target.files?.[0] ?? null)} />
        </div>
    </DialogComponent>;
}
